import Map from 'ol/Map';
import { useEffect, useState } from "react";
import { Link, useNavigate, useOutletContext, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import setTitle from "../../../components/common/SetTitle";
import LoadingSpinner from "../../../components/loadingSpinner/LoadingSpinner";
import { OutletIProps } from "../../../components/sitebase/Base.types";
import { View } from "ol";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import Fill from "ol/style/Fill";
import Stroke from "ol/style/Stroke";
import Style from "ol/style/Style";
import { MapPresenter } from "../../map/MapPresenter";
import { epsg3857 } from "../../map/maputils";
import { MapComponent } from '../../map/MapV3';
import { fetchCSRFToken } from "../../../components/common/utilities/api";
import { InlineRule } from "./Inline.types";
import { areaOfInterestToFeatures } from "./inlineGeo";
import { INLINE_MENU_LINKS } from "./InlineMenu";

export function InlineRuleDetail() {
    const { ruleId } = useParams();
    const context = useOutletContext<OutletIProps>();
    const { setExtraMenuLinks } = context;
    const navigate = useNavigate();
    const [rule, setRule] = useState<InlineRule | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [isUpdating, setIsUpdating] = useState<boolean>(false);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);
    const [aoiSource] = useState<VectorSource>(() => new VectorSource({ wrapX: true }));
    const [aoiLayer] = useState<VectorLayer>(() => new VectorLayer({
        source: aoiSource,
        style: new Style({
            fill: new Fill({ color: "rgba(0, 0, 0, 0.15)" }),
            stroke: new Stroke({ color: "red", width: 3 }),
        }),
    }));
    const [map] = useState<Map>(() => new Map({
        layers: [
            aoiLayer,
        ],
        view: new View({
            center: epsg3857({ lat: 50, lon: 15 }),
            zoom: 4,
        }),
        controls: [],
    }));
    const [presenter] = useState<MapPresenter>(() => new MapPresenter(map));

    useEffect(() => {
        setTitle("INLINE Rule Detail");
        setExtraMenuLinks(INLINE_MENU_LINKS, "Rules");
    }, [setExtraMenuLinks]);

    useEffect(() => {
        if (!ruleId) {
            toast.error("Missing rule id.");
            setLoading(false);
            return;
        }

        setLoading(true);

        const headers: Record<string, string> = {};
        if (context.user.token) {
            headers["Authorization"] = `Token ${context.user.token}`;
        }

        fetch(`/api/inline/rules/${ruleId}/`, { method: "GET", headers, credentials: "include" })
            .then(async (response) => {
                const data = await response.json().catch(() => null);
                if (!response.ok) {
                    const message = data?.message || data?.detail || JSON.stringify(data) || "Unknown error";
                    throw new Error(`HTTP ${response.status}: ${message}`);
                }
                return data as InlineRule;
            })
            .then((data) => setRule(data))
            .catch((e) => {
                console.error("Failed to load rule detail:", e);
                toast.error(`Failed to load rule: ${e instanceof Error ? e.message : String(e)}`);
            })
            .finally(() => setLoading(false));
    }, [ruleId, context.user.token]);

    useEffect(() => {
        aoiSource.clear();
        const features = areaOfInterestToFeatures(rule?.area_of_interest);
        if (features.length === 0) {
            return;
        }

        aoiSource.addFeatures(features);

        const extent = aoiSource.getExtent();
        if (extent && extent.every(Number.isFinite)) {
            map.getView().fit(extent, { padding: [30, 30, 30, 30], maxZoom: 10 });
        }
    }, [rule, aoiSource, map]);

    if (loading) {
        return <div className="py-4"><LoadingSpinner /></div>;
    }

    if (!rule) {
        return (
            <div className="container py-4">
                <div className="mb-3"><Link to="../rules">Back to rules</Link></div>
                <div className="alert alert-warning">Rule not found.</div>
            </div>
        );
    }

    const buildHeaders = async () => {
        const headers: Record<string, string> = {
            "Content-Type": "application/json",
        };
        if (context.user.token) {
            headers["Authorization"] = `Token ${context.user.token}`;
            headers["X-CSRFToken"] = await fetchCSRFToken();
        }
        return headers;
    };

    const toggleActive = async () => {
        if (!rule || isUpdating) {
            return;
        }

        setIsUpdating(true);
        try {
            const response = await fetch(`/api/inline/rules/${rule.id}/`, {
                method: "PATCH",
                headers: await buildHeaders(),
                credentials: "include",
                body: JSON.stringify({ active: !rule.active }),
            });
            const data = await response.json().catch(() => null);
            if (!response.ok) {
                const message = data?.message || data?.detail || JSON.stringify(data) || "Unknown error";
                throw new Error(`HTTP ${response.status}: ${message}`);
            }
            setRule((prev) => (prev ? { ...prev, active: !prev.active } : prev));
            toast.info(`Rule ${rule.active ? "disabled" : "enabled"}.`);
        } catch (error) {
            console.error("Failed to update rule:", error);
            toast.error(`Failed to update rule: ${error instanceof Error ? error.message : String(error)}`);
        } finally {
            setIsUpdating(false);
        }
    };

    const deleteRule = async () => {
        if (!rule || isDeleting) {
            return;
        }

        if (!confirm("Delete this rule? This cannot be undone.")) {
            return;
        }

        setIsDeleting(true);
        try {
            const response = await fetch(`/api/inline/rules/${rule.id}/`, {
                method: "DELETE",
                headers: await buildHeaders(),
                credentials: "include",
            });
            if (!response.ok) {
                const data = await response.json().catch(() => null);
                const message = data?.message || data?.detail || JSON.stringify(data) || "Unknown error";
                throw new Error(`HTTP ${response.status}: ${message}`);
            }
            toast.info("Rule deleted.");
            navigate("/inline/rules/");
        } catch (error) {
            console.error("Failed to delete rule:", error);
            toast.error(`Failed to delete rule: ${error instanceof Error ? error.message : String(error)}`);
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <div className="container py-4">
            <div className="mb-3"><Link to="../rules">Back to rules</Link></div>
            <h3 className="mb-3">{rule.name}</h3>
            <div className="d-flex flex-column flex-lg-row gap-3">
                <div className="flex-fill">
                    <div className="border rounded p-3 h-100">
                        <div><strong>ID:</strong> {rule.id}</div>
                        <div><strong>Active:</strong> <span style={{ color: rule.active ? "green" : "red" }}>{rule.active ? "✓ Yes, notifications will be sent" : "✕ No, notifications will not be sent"}</span></div>
                        <div><strong>Layer:</strong> {rule.layer_name}</div>
                        <div><strong>Grouping:</strong> {rule.notification_grouping ?? "—"}</div>
                        <div><strong>Grouping attribute:</strong> {rule.grouping_attribute}</div>
                        <div><strong>File of interest:</strong> {rule.file_of_interest ?? "—"}</div>
                        <div><strong>Latest forecast date:</strong> {rule.latest_forecast_date}</div>
                        <div className="mt-2">
                            <strong>Attributes:</strong>
                            {rule.attributes && rule.attributes.length > 0 ? (
                                <ul className="mb-0">
                                    {rule.attributes.map((attr) => (
                                        <li key={attr.id}>{attr.attribute_name} {attr.attribute_operation} {attr.attribute_value}</li>
                                    ))}
                                </ul>
                            ) : (
                                <div>—</div>
                            )}
                        </div>
                        <div className="mt-2">
                            <strong>Datasets:</strong>
                            {rule.datasets && rule.datasets.length > 0 ? (
                                <ul className="mb-0">
                                    {rule.datasets.map((ds) => (
                                        <li key={ds.id}>
                                            {ds.name} <span className="text-muted">({ds.status})</span>
                                            {ds.description ? `: ${ds.description}` : ""}
                                            {(ds.raster_min !== null || ds.raster_max !== null) ? (
                                                <span className="text-muted"> [{ds.raster_min ?? "—"} – {ds.raster_max ?? "—"}]</span>
                                            ) : null}
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <div>—</div>
                            )}
                        </div>
                        <div className="d-flex flex-wrap gap-2 mt-3">
                            <Link to={`/inline/rules/${rule.id}/edit`} className="btn btn-outline-primary">Edit rule</Link>
                            <button type="button" className={`btn ${rule.active ? "btn-warning" : "btn-outline-success"}`} onClick={toggleActive} disabled={isUpdating || isDeleting}>
                                {rule.active ? "Disable" : "Enable"} rule
                            </button>
                            <button type="button" className="btn btn-outline-danger" onClick={deleteRule} disabled={isUpdating || isDeleting}>
                                Delete rule
                            </button>
                        </div>
                    </div>
                </div>
                <div className="flex-fill">
                    {/* <div className="border rounded p-3"> */}
                    {/* <div className="mb-2"><strong>Area of interest (GeoJSON)</strong></div>
                        <pre className="mb-0" style={{ maxHeight: "50vh", overflow: "auto" }}>
                            {JSON.stringify(rule.area_of_interest, null, 2)}
                        </pre>
                    </div> */}
                    <div>
                        <MapComponent presenter={presenter} width="100%" />
                    </div>
                </div>
            </div>
        </div>
    );
}
